import { filter } from "underscore";
import NoDataText from "./NoDataText";
import { capitalizeWords } from "@/ts/utils";
import Delta from "./Delta";
import ViewAllButtonWidget from "./ViewAllButtonWidget";
import { Select, Skeleton } from "antd";
import { REVSTACK_W7_TABLE_NO_DATA_MESSAGES } from "@/ts/constants";


interface IRevstackLaggardsSectionWidget {
    drilldownData: any[];
    filterOptions: { label: string; value: string }[];
    selectedFilter: string;
    setSelectedFilter: (val: string) => void;
    drilldown: any;
    isLoading: boolean;
    size?: "small" | "large";
    noDataText?: string;
    maxRowsInGroup?: number;
}

const commonStyles = (size: string) => {
return {
    row: {
        color: "#1e1b39",
        fontSize: 14,
        fontWeight: 500,
        paddingTop: size === "large" ? "5px" : undefined,
        paddingBottom: size === "large" ? "5px" : undefined,
    },
    value: {
        color: "#1e1b39",
        fontSize: 16,
        fontWeight: 600,
    },
    change: {
        fontSize: 12,
        fontWeight: 600,
    },
    badge: {
        width: 71.96,
        height: 35,
        borderRadius: "6px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
    laggardHeader: {
        color: "#1e1b39",
        backgroundColor: "#F0F4F8",
        fontSize: 14,
        borderRadius: size === "large" ? "16px 16px 0 0" : "0px"
    },
}
}

const NoDataTextStyle = { 
    fontSize: "12px", 
    fontWeight: "500", 
    textAlign: "center" as const
}

const LoadingSkeleton = ({
    height,
    className = "",
    style = {},
}: {
    height: number;
    className?: string;
    style?: React.CSSProperties;
}) => (
    <Skeleton.Node className={className} active style={{ height, ...style }} />
);

// Common row wrapper component
const RowWrapper = ({
    children,
    isLast,
    className = "",
    style = {},
}: {
    children: React.ReactNode;
    isLast: boolean;
    className?: string;
    style?: React.CSSProperties;
}) => (
    <div
        className={`row align-items-center bg-white position-relative mb-0 py-2 ${!isLast ? "border-bottom" : ""
            } ${className}`}
        style={style}
    >
        {children}
    </div>
);


const LaggardRow = ({
    laggard,
    isLast,
    selectedFilter,
    size
}: {
    laggard: any;
    isLast: boolean;
    selectedFilter: string;
    size: string
}) => {
    const drilldownData = laggard[selectedFilter];


    return (
        <RowWrapper isLast={isLast}>
            <div
                className="col-5 px-0"
                style={{
                    ...commonStyles(size).row,
                    color: "#0078AA",
                }}
                onClick={()=>{
                    if(laggard?.valueOnClick){
                        laggard?.valueOnClick();
                    }
                }}
            >
                {capitalizeWords(laggard?.laggardField)}
            </div>
            <div className="col-3 text-end px-0" style={commonStyles(size).value}>
                {drilldownData?.value}
            </div>
            <div
                className={`col-4 text-end d-flex align-items-center justify-content-end px-0 ${drilldownData?.isPositive ? "text-success" : "text-danger"
                    }`}
                style={commonStyles(size).change}
            >
                {typeof drilldownData?.isPositive === "boolean" && <Delta isPositive={drilldownData?.isPositive} /> }  &nbsp; {drilldownData?.change}
            </div>
        </RowWrapper>
    );
};
const RevstackLaggardsSectionWidget = ({
    drilldownData,
    filterOptions,
    selectedFilter,
    setSelectedFilter,
    drilldown,
    isLoading,
    size = "small",
    noDataText = REVSTACK_W7_TABLE_NO_DATA_MESSAGES.LAGGARDS.ALL_CATEGORIES,
    maxRowsInGroup
}: IRevstackLaggardsSectionWidget) => {
    const renderLaggards = () => {

        const calculateNoDataHeight = () => {
            if (maxRowsInGroup !== undefined && maxRowsInGroup > 0) {
                return maxRowsInGroup * 36;
            }
            return undefined; // Let it size naturally if no max provided
        };

        const noDataHeight = calculateNoDataHeight();
        const noDataStyle = noDataHeight 
            ? { minHeight: `${noDataHeight}px` }
            : { minHeight: "inherit" };

        if (!drilldownData || drilldownData.length === 0) {
            return (
                <div 
                    className="d-flex align-items-center justify-content-center" 
                    style={noDataStyle}
                >
                    <NoDataText message={noDataText} style={NoDataTextStyle} />
                </div>
            );
        }


        const filteredLaggards = filter(
            drilldownData,
            (laggard) =>
                laggard[selectedFilter] &&
                laggard[selectedFilter].value !== undefined
        );

        if (filteredLaggards.length === 0) {
            return (
                <div 
                    className="d-flex align-items-center justify-content-center" 
                    style={noDataStyle}
                >
                    <NoDataText message={noDataText} style={NoDataTextStyle} />
                </div>
            );
        }

        const limit = drilldown?.limit || filteredLaggards.length;
        const displayLaggards = filteredLaggards.slice(0, limit);

        return (
            <>
                {displayLaggards.map((laggard, index) => (
                    <LaggardRow
                        key={index}
                        laggard={laggard}
                        selectedFilter={selectedFilter}
                        isLast={index === displayLaggards.length - 1}
                        size={size}
                    />
                ))}
                <ViewAllButtonWidget />
            </>
        );
    };

    return (
        <>
            <div
                className={`d-flex justify-content-between align-items-center px-4 py-2`}
                style={commonStyles(size).laggardHeader}
            >
                <h6 className="mb-0">{drilldown?.title}</h6>
                {drilldown?.hasFilter && (
                    <Select
                        size="small"
                        className="revstack-summary-select"
                        options={filterOptions}
                        style={{ width: 120 }}
                        value={selectedFilter}
                        onChange={setSelectedFilter}
                        optionRender={(option) => (
                            <div style={{ fontSize: "12px" }}>
                                {option.label}
                            </div>
                        )}
                        disabled={isLoading}
                    />
                )}
            </div>

            {isLoading ? (
                <LoadingSkeleton height={140} className="p-2 w-100" />
            ) : (
                <div className="px-4 mx-2">{renderLaggards()}</div>
            )}
        </>
    );
};

export default RevstackLaggardsSectionWidget